<template>
  <div class="table-container">
    <div class="table-header">
      <div class="table-title">
        {{$t('program')}} ({{ filteredPrograms.length }})
      </div>
      <div class="table-actions">
        <button
            v-if="showViewAll && filteredPrograms.length > 3"
            @click="toggleExpanded"
            class="view-all-btn"
        >
          {{ isExpanded ? $t('show_less') : $t('show_more') }}
        </button>
      </div>
    </div>

    <div class="desktop-table" :class="{ 'expanded': isExpanded }">
      <table class="content-table">
        <thead class="header-table">
        <tr class="trhead">
          <th @click="sortBy('title')" :class="getSortClass('title')">
            {{ $t('program') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('title')"
            >
          </th>
          <th @click="sortBy('status')" :class="getSortClass('status')">
            {{ $t('status') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('status')"
            >
          </th>
          <th @click="sortBy('assignedCount')" :class="getSortClass('assignedCount')">
            {{ $t('assigned') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('assignedCount')"
            >
          </th>

          <th @click="sortBy('openCount')" :class="getSortClass('openCount')">
            {{ $t('open_count') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('openCount')"
            >
          </th>

          <th @click="sortBy('completionRate')" :class="getSortClass('completionRate')">
            {{ $t('completion_rate') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('completionRate')"
            >
          </th>
          <th @click="sortBy('publishDate')" :class="getSortClass('publishDate')">
            {{ $t('publish_date') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('publishDate')"
            >
          </th>

          <th @click="sortBy('publishEndDate')" :class="getSortClass('publishEndDate')">
            {{ $t('publish_finish_date') }}
            <img
                src="../../assets/images/status.svg"
                alt="sort"
                :class="getSortIconClass('publishEndDate')"
            >
          </th>
        </tr>
        </thead>

        <tbody class="tbody-container">
        <tr v-if="loading">
          <td colspan="7" class="table-loading">
            <div class="loading-content">
              <LottieAnimation
                  :animation-data="loadingAnim"
                  :auto-play="true"
                  :loop="true"
                  :speed="1"
                  class="lottie-loader"
              />
            </div>
          </td>
        </tr>

        <tr v-else-if="!programs || programs.length === 0">
          <td colspan="7" class="table-loading">
            <div class="empty-state">
              <span class="no-data-found">{{ $t('no_programs_founds') }}</span>
            </div>
          </td>
        </tr>

        <tr
            v-else
            v-for="program in displayedPrograms"
            :key="program.id"
            class="tr-class"
        >
          <td class="td-style td-row name-row">
            <div class="program-info">
              <img
                  :src="program.image || getDefaultImage()"
                  :alt="program.title"
                  class="program-image"
                  @click="handleProgramClick(program)"
                  @error="handleImageError"
              />
              <div class="program-details">
                <span
                    class="table-link program-title"
                    @click="handleProgramClick(program)"
                >
                  {{ program.title || $t('untitled_program') }}
                </span>
                <div class="program-subtitle" @click.stop>{{ program.subtitle || program.description || '' }}</div>
              </div>
            </div>
          </td>

          <td class="td-style">
            <div class="status-tag" :class="getStatusClass(program.status)">
              <div class="status-dot" :class="getStatusClass(program.status)"></div>
              <span>{{ getStatusText(program.status) }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="count-wrapper">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 17.5H14.5V16C14.5 15.4033 14.2629 14.831 13.841 14.409C13.419 13.9871 12.8467 13.75 12.25 13.75H7.75C7.15326 13.75 6.58097 13.9871 6.15901 14.409C5.73705 14.831 5.5 15.4033 5.5 16V17.5H4V16C4 15.0054 4.39509 14.0516 5.09835 13.3483C5.80161 12.6451 6.75544 12.25 7.75 12.25H12.25C13.2446 12.25 14.1984 12.6451 14.9017 13.3483C15.6049 14.0516 16 15.0054 16 16V17.5ZM10 10.75C9.40905 10.75 8.82389 10.6336 8.27792 10.4075C7.73196 10.1813 7.23588 9.84984 6.81802 9.43198C6.40016 9.01412 6.06869 8.51804 5.84254 7.97208C5.6164 7.42611 5.5 6.84095 5.5 6.25C5.5 5.65905 5.6164 5.07389 5.84254 4.52792C6.06869 3.98196 6.40016 3.48588 6.81802 3.06802C7.23588 2.65016 7.73196 2.31869 8.27792 2.09254C8.82389 1.8664 9.40905 1.75 10 1.75C11.1935 1.75 12.3381 2.22411 13.182 3.06802C14.0259 3.91193 14.5 5.05653 14.5 6.25C14.5 7.44347 14.0259 8.58807 13.182 9.43198C12.3381 10.2759 11.1935 10.75 10 10.75V10.75ZM10 9.25C10.7956 9.25 11.5587 8.93393 12.1213 8.37132C12.6839 7.80871 13 7.04565 13 6.25C13 5.45435 12.6839 4.69129 12.1213 4.12868C11.5587 3.56607 10.7956 3.25 10 3.25C9.20435 3.25 8.44129 3.56607 7.87868 4.12868C7.31607 4.69129 7 5.45435 7 6.25C7 7.04565 7.31607 7.80871 7.87868 8.37132C8.44129 8.93393 9.20435 9.25 10 9.25V9.25Z" fill="#475467"/>
              </svg>
              <span class="test-class">{{ formatNumber(program.assignedCount || 0) }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="count-wrapper">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.25 16.75C3.05109 16.75 2.86032 16.671 2.71967 16.5303C2.57902 16.3897 2.5 16.1989 2.5 16V4C2.5 3.80109 2.57902 3.61032 2.71967 3.46967C2.86032 3.32902 3.05109 3.25 3.25 3.25H8.8105L10.3105 4.75H16C16.1989 4.75 16.3897 4.82902 16.5303 4.96967C16.671 5.11032 16.75 5.30109 16.75 5.5V7.75H15.25V6.25H9.6895L8.1895 4.75H4V13.7485L5.125 9.25H17.875L16.1425 16.1823C16.1019 16.3444 16.0082 16.4884 15.8764 16.5913C15.7446 16.6941 15.5822 16.75 15.415 16.75H3.25V16.75ZM15.9535 10.75H6.2965L5.1715 15.25H14.8285L15.9535 10.75Z" fill="#475467"/>
              </svg>
              <span class="test-class">{{ formatNumber(program.openCount || 0) }}</span>
            </div>
          </td>

          <td class="td-style">
            <div
                class="progress-wrapper"
                @click.stop="openFunnelModal(program, $event)"
            >
              <div class="progress-bar">
                <div
                    class="progress-fill"
                    :style="{ width: `${program.completionRate || 0}%` }"
                    :class="getProgressClass(program.completionRate)"
                ></div>
              </div>
              <span class="test-class progress-text">{{ Math.round(program.completionRate || 0) }}%</span>
            </div>
          </td>

          <td class="td-style">
            <div class="date-wrapper">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.75 3.25H16.75C16.9489 3.25 17.1397 3.32902 17.2803 3.46967C17.421 3.61032 17.5 3.80109 17.5 4V16C17.5 16.1989 17.421 16.3897 17.2803 16.5303C17.1397 16.671 16.9489 16.75 16.75 16.75H3.25C3.05109 16.75 2.86032 16.671 2.71967 16.5303C2.57902 16.3897 2.5 16.1989 2.5 16V4C2.5 3.80109 2.57902 3.61032 2.71967 3.46967C2.86032 3.32902 3.05109 3.25 3.25 3.25H6.25V1.75H7.75V3.25H12.25V1.75H13.75V3.25ZM12.25 4.75H7.75V6.25H6.25V4.75H4V7.75H16V4.75H13.75V6.25H12.25V4.75ZM16 9.25H4V15.25H16V9.25Z" fill="#475467"/>
              </svg>
              <span class="test-class">{{ formatDate(program.publishDate) }}</span>
            </div>
          </td>

          <td class="td-style">
            <div class="date-wrapper">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.75 3.25H16.75C16.9489 3.25 17.1397 3.32902 17.2803 3.46967C17.421 3.61032 17.5 3.80109 17.5 4V16C17.5 16.1989 17.421 16.3897 17.2803 16.5303C17.1397 16.671 16.9489 16.75 16.75 16.75H3.25C3.05109 16.75 2.86032 16.671 2.71967 16.5303C2.57902 16.3897 2.5 16.1989 2.5 16V4C2.5 3.80109 2.57902 3.61032 2.71967 3.46967C2.86032 3.32902 3.05109 3.25 3.25 3.25H6.25V1.75H7.75V3.25H12.25V1.75H13.75V3.25ZM12.25 4.75H7.75V6.25H6.25V4.75H4V7.75H16V4.75H13.75V6.25H12.25V4.75ZM16 9.25H4V15.25H16V9.25Z" fill="#475467"/>
              </svg>
              <span class="test-class">{{ formatDate(program.publishEndDate) }}</span>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="mobile-cards">
      <div v-if="loading" class="mobile-loading">
        <div class="loading-content">
          <LottieAnimation
              :animation-data="loadingAnim"
              :auto-play="true"
              :loop="true"
              :speed="1"
              class="lottie-loader"
          />
        </div>
      </div>

      <div v-else-if="!programs || programs.length === 0" class="mobile-empty">
        <div class="empty-state">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="48" height="48" rx="24" fill="#F9FAFB"/>
            <path d="M24 16C22.9 16 22 16.9 22 18C22 19.1 22.9 20 24 20C25.1 20 26 19.1 26 18C26 16.9 25.1 16 24 16Z" fill="#667085"/>
            <path d="M24 22C21.79 22 20 23.79 20 26V30H28V26C28 23.79 26.21 22 24 22Z" fill="#667085"/>
          </svg>
          <span class="text">{{ $t('no_programs_found') }}</span>
        </div>
      </div>

      <div
          v-else
          v-for="program in displayedPrograms"
          :key="program.id"
          class="program-card"
          @click="handleProgramClick(program)"
      >
        <div class="card-header">
          <div class="program-info">
            <img
                :src="program.image || getDefaultImage()"
                :alt="program.title"
                class="program-image"
                @error="handleImageError"
            />
            <div class="program-details">
              <h3 class="program-title">{{ program.title || $t('untitled_program') }}</h3>
              <p class="program-subtitle">{{ program.subtitle || program.description || '' }}</p>
            </div>
          </div>
          <div class="status-tag" :class="getStatusClass(program.status)">
            <div class="status-dot" :class="getStatusClass(program.status)"></div>
            <span>{{ getStatusText(program.status) }}</span>
          </div>
        </div>

        <div class="card-body">
          <div class="card-row">
            <div class="card-item">
              <span class="card-label">{{ $t('opens') }}</span>
              <span class="card-value">{{ formatNumber(program.openCount || 0) }}</span>
            </div>
            <div class="card-item">
              <span class="card-label">{{ $t('assigned') }}</span>
              <span class="card-value">{{ formatNumber(program.assignedCount || 0) }}</span>
            </div>
          </div>

          <div class="card-row">
            <div class="card-item">
              <span class="card-label">{{ $t('completion') }}</span>
              <div
                  class="progress-wrapper-mobile"
                  @click="openFunnelModal(program, $event)"
              >
                <div class="progress-bar">
                  <div
                      class="progress-fill"
                      :style="{ width: `${program.completionRate || 0}%` }"
                      :class="getProgressClass(program.completionRate)"
                  ></div>
                </div>
                <span class="progress-text">{{ Math.round(program.completionRate || 0) }}%</span>
              </div>
            </div>

            <div class="card-item">
              <span class="card-label">{{ $t('date') }}</span>
              <span class="card-value">{{ formatDate(program.publishDate) }}</span>
            </div>

            <div class="card-item">
              <span class="card-label">{{ $t('publish_finish_date') }}</span>
              <span class="card-value">{{ formatDate(program.publishEndDate) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FunnelChart
        :is-open="showFunnelModal"
        :program="selectedProgram"
        :trigger-element="triggerElement"
        @close="closeFunnelModal"
    />
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import FunnelChart from '@/components/charts/FunnelChart.vue'
import { LottieAnimation } from 'lottie-web-vue'
import loadingAnim from '@/assets/animations/loading.json'

const { t } = useI18n()
const router = useRouter()

const props = defineProps({
  programs: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Programs'
  },
  loading: {
    type: Boolean,
    default: false
  },
  showViewAll: {
    type: Boolean,
    default: true
  },
  pageSize: {
    type: Number,
    default: 10
  },
  searchQuery: {
    type: String,
    default: ''
  },
  statusFilter: {
    type: String,
    default: 'all'
  }
})

const emit = defineEmits(['programSelect', 'viewAll', 'sortChange', 'funnelModalStateChange'])

const isExpanded = ref(false)
const sortField = ref(null)
const sortOrder = ref('asc')

const showFunnelModal = ref(false)
const selectedProgram = ref(null)
const triggerElement = ref(null)

watch(showFunnelModal, (isOpen) => {
  emit('funnelModalStateChange', isOpen)
})

const handleProgramClick = (program) => {
  if (!program?.id) {
    return
  }
  router.push(`/journey-programs/${program.id}`)
}

const openFunnelModal = (program, event) => {
  if (!program) return

  event.stopPropagation()

  selectedProgram.value = program

  triggerElement.value = event.currentTarget
  showFunnelModal.value = true
}

const closeFunnelModal = () => {
  showFunnelModal.value = false
  selectedProgram.value = null
  triggerElement.value = null
}

const filteredPrograms = computed(() => {
  if (!Array.isArray(props.programs)) {
    return []
  }

  let filtered = [...props.programs]

  if (props.searchQuery && typeof props.searchQuery === 'string') {
    const query = props.searchQuery.toLowerCase().trim()
    if (query) {
      filtered = filtered.filter(program => {
        const title = String(program?.title ?? '').toLowerCase()
        const subtitle = String(program?.subtitle ?? '').toLowerCase()
        const description = String(program?.description ?? '').toLowerCase()

        return title.includes(query) || subtitle.includes(query) || description.includes(query)
      })
    }
  }

  if (props.statusFilter && props.statusFilter !== 'all') {
    filtered = filtered.filter(program => program?.status === props.statusFilter)
  }

  return filtered
})

const sortedPrograms = computed(() => {
  if (!Array.isArray(filteredPrograms.value)) {
    return []
  }

  const programs = [...filteredPrograms.value]

  if (sortField.value === null) {
    return programs
  }

  programs.sort((a, b) => {
    if (!a || !b) return 0

    let aVal = a[sortField.value]
    let bVal = b[sortField.value]

    if (aVal === null || aVal === undefined) aVal = ''
    if (bVal === null || bVal === undefined) bVal = ''

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase()
      bVal = String(bVal).toLowerCase()
    }

    if (sortField.value === 'publishDate' || sortField.value === 'publishEndDate') {
      const dateA = new Date(aVal)
      const dateB = new Date(bVal)

      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return sortOrder.value === 'asc'
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime()
      }
    }

    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortOrder.value === 'asc' ? aVal - bVal : bVal - aVal
    }

    if (sortOrder.value === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
    }
  })

  return programs
})

const displayedPrograms = computed(() => {
  if (!Array.isArray(sortedPrograms.value)) {
    return []
  }

  if (!isExpanded.value && sortedPrograms.value.length > 3) {
    return sortedPrograms.value.slice(0, 3)
  }

  return sortedPrograms.value
})

const hasMorePrograms = computed(() => {
  return Array.isArray(sortedPrograms.value) && sortedPrograms.value.length > 3
})

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value
}

const sortBy = (field) => {
  if (!field || typeof field !== 'string') {
    return
  }

  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'asc'
  }

  emit('sortChange', { field: sortField.value, order: sortOrder.value })
}

const getSortClass = (field) => {
  if (!field || sortField.value !== field) return ''
  return `sort-active sort-${sortOrder.value}`
}

const getSortIconClass = (field) => {
  if (!field || sortField.value !== field) return 'sort-icon'
  return `sort-icon sort-icon-${sortOrder.value}`
}

const getStatusClass = (status) => {
  if (!status || typeof status !== 'string') {
    return 'Draft'
  }

  const statusClasses = {
    'published': 'Active',
    'yayinda': 'Active',
    'draft': 'Draft',
    'taslak': 'Draft',
    'archived': 'Draft',
    'arsiv': 'Draft'
  }

  return statusClasses[status.toLowerCase()] || 'Draft'
}

const getStatusText = (status) => {
  if (!status || typeof status !== 'string') {
    return t('unknown')
  }

  const statusMap = {
    'published': 'published',
    'yayinda': 'published',
    'draft': 'draft',
    'taslak': 'draft',
    'archived': 'archived',
    'arsiv': 'archived'
  }

  const key = statusMap[status.toLowerCase()] || 'unknown'
  return t(key)
}

const getProgressClass = (rate) => {
  const numRate = Number(rate) || 0

  if (numRate >= 80) return 'progress-excellent'
  if (numRate >= 60) return 'progress-good'
  if (numRate >= 40) return 'progress-moderate'
  return 'progress-low'
}

const formatNumber = (num) => {
  const number = Number(num)

  if (isNaN(number)) {
    return '0'
  }

  try {
    return new Intl.NumberFormat('en-US').format(number)
  } catch (error) {
    return String(number)
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return '-'

  try {
    const date = new Date(dateStr)

    if (isNaN(date.getTime())) {
      return '-'
    }

    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  } catch (error) {
    return '-'
  }
}

const getDefaultImage = () => {
  return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iOCIgZmlsbD0iI0Y5RkFGQiIvPgo8cGF0aCBkPSJNMjAgMTJDMTguOSAxMiAxOCAxMi45IDE4IDE0QzE4IDE1LjEgMTguOSAxNiAyMCAxNkMyMS4xIDE2IDIyIDE1LjEgMjIgMTRDMjIgMTIuOSAyMS4xIDEyIDIwIDEyWiIgZmlsbD0iIzY2NzA4NSIvPgo8cGF0aCBkPSJNMjAgMThDMTcuNzkgMTggMTYgMTkuNzkgMTYgMjJWMjZIMjRWMjJDMjQgMTkuNzkgMjIuMjEgMTggMjAgMThaIiBmaWxsPSIjNjY3MDg1Ii8+Cjwvc3ZnPgo='
}

const handleImageError = (event) => {
  if (!event?.target) {
    return
  }

  event.target.src = getDefaultImage()
}

watch(() => props.programs, (newPrograms) => {
  if (!Array.isArray(newPrograms)) {
  }
  isExpanded.value = false
  sortField.value = null
}, { deep: true })

watch(() => props.searchQuery, (newQuery) => {
  if (newQuery !== undefined && newQuery !== null) {
    isExpanded.value = false
  }
})

watch(() => props.statusFilter, (newFilter) => {
  if (newFilter !== undefined && newFilter !== null) {
    isExpanded.value = false
  }
})
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 12px;
  border: 1px solid #EAECF0;
  overflow: hidden;
  padding: 16px;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 10px;
  background: transparent;
  margin-bottom: 5px;
}

.table-title {
  font-size: 16px;
  font-weight: 500;
  color: #101828;
}

.view-all-btn {
  color: #4367FF;
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s;
}

.view-all-btn:hover {
  background: #F0F4FF;
}

.desktop-table {
  overflow-x: auto;
  overflow-y: visible;
  transition: all 0.3s ease;
}

.desktop-table:not(.expanded) {
  max-height: fit-content;
}

.desktop-table.expanded {
  max-height: 80vh;
  overflow-y: auto;
}

.content-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
}

.header-table {
  background: #F9FAFB;
  position: sticky;
  top: 0;
  z-index: 10;
}

.trhead th {
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 500;
  color: #475467;
  border-bottom: 1px solid #EAECF0;
  cursor: pointer;
  position: relative;
  font-family: 'euclid_semi_bold', 'sans-serif';
  user-select: none;
}

.trhead th:hover {
  background: #F2F4F7;
}

.sort-icon {
  margin-left: 4px;
  transition: all 0.2s;
  color: #9CA3AF;
}

.sort-active .sort-icon {
  color: #4367FF;
}

.sort-icon-asc {
  transform: rotate(180deg);
}

.sort-icon-desc {
  transform: rotate(0deg);
}

.tbody-container {
  background: white;
}

.tr-class {
  cursor: pointer;
  transition: background-color 0.15s;
}

.tr-class:hover {
  background: #F8F9FA;
}

.td-style {
  padding: 12px 16px;
  border-bottom: 1px solid #F2F4F7;
  vertical-align: middle;
  text-align: left;
}

.td-row {
  cursor: pointer;
}

.name-row span {
  font-family: euclid_medium, sans-serif;
  color: #000;
  text-align: left;
}

.name-row:hover span {
  color: #4A68F8;
  text-align: left;
}

.program-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.program-image {
  width: 48px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  background: #F9FAFB;
  flex-shrink: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.program-image:hover {
  opacity: 0.8;
}

.program-details {
  min-width: 0;
  overflow: hidden;
}

.program-title {
  font-weight: 500;
  color: #101828;
  font-size: 14px;
  display: block;
  margin-bottom: 4px;
  cursor: pointer;
  transition: color 0.2s;
}

.program-title:hover {
  color: #4367FF;
}

.program-subtitle {
  font-size: 12px;
  color: #667085;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  max-width: 200px;
}

.status-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #040c2d;
  border-radius: 8px;
  padding: 6px 12px;
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  min-width: 80px;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-tag.Draft {
  background-color: #fad9c6;
}

.status-tag.Active {
  background-color: #ddfbde;
}

.status-dot.Draft {
  background-color: #f09460;
}

.status-dot.Active {
  background-color: #3da33e;
}

.table-link {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #000000;
}

.test-class {
  font-family: euclid_semi_bold, sans-serif;
  font-size: 12px;
  color: #667085;
}

.count-wrapper, .date-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}

.progress-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  padding: 4px;
  border-radius: 6px;
  margin: -4px;
}

.progress-wrapper:hover {
  background: #F9FAFB;
}

.progress-bar {
  width: 60px;
  height: 6px;
  background: #F2F4F7;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.progress-excellent {
  background: #22C55E;
}

.progress-good {
  background: #4367FF;
}

.progress-moderate {
  background: #F59E0B;
}

.progress-low {
  background: #EF4444;
}

.progress-text {
  font-size: 11px;
  font-weight: 500;
  color: #475467;
  min-width: 32px;
}

.table-loading {
  padding: 60px 20px;
  text-align: center;
}

.loading-content, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #667085;
}

.lottie-loader {
  width: 80px;
  height: 80px;
}

.text {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
}

.mobile-cards {
  display: none;
}

.mobile-loading, .mobile-empty {
  padding: 40px 20px;
  text-align: center;
}

.program-card {
  background: #FAFBFC;
  border: 1px solid #E4E7EC;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.program-card:hover {
  background: #F2F4F7;
  border-color: #4367FF;
}

.program-card:last-child {
  margin-bottom: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 12px;
}

.card-header .program-info {
  flex: 1;
  min-width: 0;
}

.card-header .program-details {
  min-width: 0;
}

.card-header .program-title {
  font-size: 15px;
  font-weight: 600;
  color: #101828;
  margin-bottom: 4px;
}

.card-header .program-subtitle {
  font-size: 12px;
  color: #667085;
  max-width: none;
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  line-height: 1.4;
}

.card-header .status-tag {
  flex-shrink: 0;
  font-size: 11px;
  padding: 4px 8px;
  min-width: auto;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
}

.card-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-label {
  font-size: 11px;
  color: #667085;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-value {
  font-size: 14px;
  color: #101828;
  font-weight: 600;
}

.progress-wrapper-mobile {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  margin: -4px;
  transition: background 0.2s;
}

.progress-wrapper-mobile:hover {
  background: #F2F4F7;
}

.progress-wrapper-mobile .progress-bar {
  flex: 1;
  min-width: 40px;
}

.progress-wrapper-mobile .progress-text {
  font-size: 12px;
  font-weight: 600;
  color: #101828;
  min-width: 28px;
}

@media (max-width: 1200px) {
  .desktop-table.expanded {
    max-height: 60vh;
  }
}

@media (max-width: 1024px) {
  .table-container {
    padding: 12px;
  }

  .program-subtitle {
    max-width: 150px;
  }

  .progress-bar {
    width: 50px;
  }

  .desktop-table.expanded {
    max-height: 70vh;
  }
}

@media (max-width: 768px) {
  .desktop-table {
    display: none;
  }

  .mobile-cards {
    display: block;
  }

  .table-header {
    padding: 0;
    margin-bottom: 16px;
  }

  .table-title {
    font-size: 18px;
  }

  .view-all-btn {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .table-container {
    padding: 8px;
    border-radius: 8px;
  }

  .program-card {
    padding: 12px;
    border-radius: 8px;
  }

  .card-header .program-info .program-image {
    width: 40px;
    height: 32px;
  }

  .card-header .program-title {
    font-size: 14px;
  }

  .card-row {
    gap: 12px;
  }

  .card-item {
    gap: 2px;
  }

  .card-label {
    font-size: 10px;
  }

  .card-value {
    font-size: 13px;
  }
}
</style>
